import React, { useState } from 'react';
import { X, Plus, Trash2, AlertCircle } from 'lucide-react';
import { PlaceholderBinRow } from '../../../../types/itemTypeChange';

interface SplitEntry {
  serialNo?: string;
  batchNo?: string;
  quantity: number;
  expiryDate?: string | null;
}

interface Props {
  row: PlaceholderBinRow;
  isSerial: boolean;
  isBatch: boolean;
  requiresExpiry: boolean;
  onCancel: () => void;
  onConfirm: (splits: SplitEntry[]) => void;
}

export const PlaceholderSplitDialog: React.FC<Props> = ({
  row,
  isSerial,
  isBatch,
  requiresExpiry,
  onCancel,
  onConfirm,
}) => {
  const initial: SplitEntry[] = isSerial
    ? Array(Math.round(row.quantity))
        .fill(null)
        .map(() => ({ serialNo: '', quantity: 1 }))
    : [{ batchNo: '', quantity: row.quantity, expiryDate: row.expiryDate?.substring(0, 10) ?? null }];

  const [entries, setEntries] = useState<SplitEntry[]>(initial);
  const sum = entries.reduce((s, e) => s + (e.quantity || 0), 0);
  const sumOk = Math.abs(sum - row.quantity) < 0.001;

  const update = (idx: number, update: Partial<SplitEntry>) => {
    setEntries((e) => e.map((x, i) => (i === idx ? { ...x, ...update } : x)));
  };

  const add = () => {
    if (isSerial) {
      setEntries((e) => [...e, { serialNo: '', quantity: 1 }]);
    } else {
      setEntries((e) => [...e, { batchNo: '', quantity: 0, expiryDate: null }]);
    }
  };

  const remove = (idx: number) => {
    setEntries((e) => e.filter((_, i) => i !== idx));
  };

  // Validering
  const validation = (() => {
    const errors: string[] = [];
    if (!sumOk) {
      errors.push(`Sum av mengder (${sum}) må være ${row.quantity}.`);
    }
    if (isSerial) {
      const serials = entries.map((e) => e.serialNo?.trim() ?? '').filter((s) => s.length > 0);
      if (serials.length !== entries.length) {
        errors.push('Alle serienumre må fylles ut.');
      }
      if (new Set(serials).size !== serials.length) {
        errors.push('Duplikate serienumre er ikke tillatt.');
      }
    }
    if (isBatch) {
      const batches = entries.map((e) => e.batchNo?.trim() ?? '').filter((b) => b.length > 0);
      if (batches.length !== entries.length) {
        errors.push('Alle batch-nummer må fylles ut.');
      }
      if (new Set(batches).size !== batches.length) {
        errors.push('Duplikate batch-nummer i samme lokasjon er ikke tillatt.');
      }
      if (requiresExpiry && entries.some((e) => !e.expiryDate)) {
        errors.push('Utløpsdato er påkrevd for alle batcher.');
      }
      if (entries.some((e) => e.quantity <= 0)) {
        errors.push('Alle mengder må være > 0.');
      }
    }
    return errors;
  })();

  const canConfirm = validation.length === 0;

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm(entries);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Splitt rad</h3>
            <p className="text-xs text-gray-600 mt-0.5">
              Lokasjon <span className="font-mono">{row.binCode}</span> · Total:{' '}
              <span className="font-semibold">{row.quantity}</span>
            </p>
          </div>
          <button
            onClick={onCancel}
            className="p-1 text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          <div className="space-y-2">
            {entries.map((entry, idx) => (
              <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <span className="text-xs text-gray-500 w-6">#{idx + 1}</span>
                {isSerial && (
                  <input
                    type="text"
                    value={entry.serialNo ?? ''}
                    onChange={(e) => update(idx, { serialNo: e.target.value })}
                    placeholder="Serienummer"
                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs font-mono focus:ring-1 focus:ring-red-500"
                    autoFocus={idx === 0}
                  />
                )}
                {isBatch && (
                  <>
                    <input
                      type="text"
                      value={entry.batchNo ?? ''}
                      onChange={(e) => update(idx, { batchNo: e.target.value })}
                      placeholder="Batch-nummer"
                      className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs font-mono focus:ring-1 focus:ring-red-500"
                      autoFocus={idx === 0}
                    />
                    <input
                      type="number"
                      step="any"
                      value={entry.quantity}
                      onChange={(e) =>
                        update(idx, { quantity: parseFloat(e.target.value) || 0 })
                      }
                      placeholder="Antall"
                      className="w-24 px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-red-500"
                    />
                    {requiresExpiry && (
                      <input
                        type="date"
                        value={entry.expiryDate ?? ''}
                        onChange={(e) =>
                          update(idx, { expiryDate: e.target.value || null })
                        }
                        className="px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-red-500"
                      />
                    )}
                  </>
                )}
                <button
                  onClick={() => remove(idx)}
                  disabled={entries.length <= 1}
                  className="p-1 text-red-600 hover:bg-red-50 rounded disabled:opacity-30"
                  title="Fjern"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>

          {isBatch && (
            <button
              onClick={add}
              className="mt-2 inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800"
            >
              <Plus className="h-3 w-3" />
              Legg til batch
            </button>
          )}

          <div className="mt-3 p-2 bg-gray-50 rounded text-xs flex items-center justify-between">
            <span className="text-gray-600">
              Sum: <span className={sumOk ? 'text-green-700 font-semibold' : 'text-red-700 font-semibold'}>
                {sum}
              </span>{' '}
              / {row.quantity}
            </span>
            {!sumOk && (
              <span className="text-red-600 text-[11px]">
                Avvik: {(sum - row.quantity).toFixed(2)}
              </span>
            )}
          </div>

          {validation.length > 0 && (
            <div className="mt-3 bg-amber-50 border border-amber-200 rounded p-2 space-y-0.5">
              <div className="flex items-center gap-1 text-amber-800 text-xs font-semibold">
                <AlertCircle className="h-3 w-3" />
                Valideringsfeil
              </div>
              {validation.map((err, i) => (
                <p key={i} className="text-xs text-amber-800 ml-4">
                  • {err}
                </p>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Avbryt
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Bekreft splitt
          </button>
        </div>
      </div>
    </div>
  );
};
